/**
 * Chiamate alle API dell'agente «voce» (ADR 0028), servite da `agents`.
 *
 * La chiave di ElevenLabs non passa mai di qui: il backend restituisce solo il token
 * di conversazione (o l'indirizzo firmato della chat) e le variabili da passare
 * all'SDK, fra cui il token di sessione vocale che gli strumenti usano per leggere il
 * set. Nessuno di questi valori si salva nel browser.
 *
 * Provvisorio dichiarato, come gli altri client: percorsi scritti a mano invece che
 * generati dagli schemi OpenAPI. `voice.test.ts` presidia metodo, percorso e corpo.
 */

import { ApiError, type ApiClient } from './client';

/** Codice del 422 quando il progetto scelto non è nel set. */
export const PROJECT_NOT_IN_SET = 'project_not_in_set';

/** Modalità della conversazione. */
export type VoiceMode = 'voice' | 'text';

/** Risposta all'avvio di una conversazione. */
export interface VoiceSessionStart {
  /** Sessione vocale di institor. */
  session_id: string;
  /** Modalità. */
  mode: VoiceMode;
  /** Agente ElevenLabs. */
  agent_id: string;
  /** Token WebRTC della conversazione vocale. */
  conversation_token: string | null;
  /** Indirizzo WebSocket firmato della chat. */
  signed_url: string | null;
  /** Variabili da passare all'avvio dell'SDK. */
  dynamic_variables: Record<string, string>;
  /** Minuti di voce residui; `null` se illimitati. */
  minutes_left: number | null;
  /** Scadenza del token di sessione, ISO 8601. */
  expires_at: string;
  /** Durata massima della conversazione, in secondi. */
  max_duration_seconds: number;
  /** Voce scelta dall'utente, da passare all'SDK come override `tts.voiceId`. */
  voice_id?: string;
}

/** Una voce dell'agente vocale. */
export interface VoiceOption {
  /** Chiave da salvare. */
  key: string;
  /** Nome della voce. */
  name: string;
  /** `female` o `male`. */
  gender: 'female' | 'male';
}

/** Voci ammesse e voce scelta. */
export interface VoiceChoice {
  /** Voci, nell'ordine in cui mostrarle. */
  voices: VoiceOption[];
  /** Chiave della voce in uso. */
  selected: string;
}

/** Un turno della trascrizione. */
export interface VoiceTurn {
  /** `user` o `agent`. */
  role: string;
  /** Testo. */
  message: string;
  /** Secondo della conversazione. */
  time_in_call_secs: number | null;
}

/** Una conversazione dello storico. */
export interface VoiceSessionSummary {
  /** Sessione. */
  id: string;
  /** Set. */
  agent_set_id: string;
  /** Modalità. */
  mode: VoiceMode;
  /** `active` o `ended`. */
  status: string;
  /** Chi ha parlato. */
  user_label: string;
  /** Inizio, ISO 8601. */
  started_at: string;
  /** Fine, ISO 8601. */
  ended_at: string | null;
  /** Durata riconosciuta, in secondi. */
  duration_seconds: number;
  /** Minuti conteggiati. */
  billed_minutes: number;
  /** Riassunto, vuoto se non arrivato. */
  summary: string;
  /** Trascrizione. */
  transcript: VoiceTurn[];
}

/** Minuti di voce del mese. */
export interface VoiceUsage {
  /** True se la voce è attiva su questa installazione. */
  enabled: boolean;
  /** Mese UTC, `AAAA-MM`. */
  month: string;
  /** Piano. */
  plan: string;
  /** True per un amministratore. */
  unrestricted: boolean;
  /** Minuti usati nel mese. */
  used_minutes: number;
  /** Quota del piano; `null` se illimitata. */
  plan_minutes: number | null;
  /** Minuti acquistati disponibili. */
  credits: number;
  /** Residuo; `null` se illimitato. */
  minutes_left: number | null;
}

/** Richiesta di avvio. */
export interface StartVoiceRequest {
  /** Set da raccontare. */
  setId: string;
  /** Modalità. */
  mode: VoiceMode;
  /** Progetto del set su cui concentrarsi; omesso, tutti. */
  projectId?: string;
}

/** Client dell'agente vocale. */
export class VoiceApi {
  /**
   * @param client Client HTTP della sessione.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Apre una conversazione.
   *
   * Il progetto aperto nella pagina è quello da cui la voce parte (ADR 0028 §3.3). Se nel
   * frattempo è uscito dal set (422 `project_not_in_set`), la conversazione si apre su
   * tutto il set invece di fallire.
   *
   * @param request Set, modalità e progetto.
   * @returns Token o indirizzo firmato, variabili e minuti residui.
   */
  async startSession(request: StartVoiceRequest): Promise<VoiceSessionStart> {
    const body = { set_id: request.setId, mode: request.mode };
    if (request.projectId === undefined) {
      return this.client.request<VoiceSessionStart>('POST', '/api/v1/voice/sessions', body);
    }
    try {
      return await this.client.request<VoiceSessionStart>('POST', '/api/v1/voice/sessions', {
        ...body,
        project_id: request.projectId,
      });
    } catch (error) {
      if (error instanceof ApiError && error.code === PROJECT_NOT_IN_SET) {
        return this.client.request<VoiceSessionStart>('POST', '/api/v1/voice/sessions', body);
      }
      throw error;
    }
  }

  /**
   * Chiude una conversazione dal browser (il webhook di ElevenLabs, se arriva, prevale).
   *
   * @param sessionId Sessione.
   * @param durationSeconds Durata misurata dal browser.
   * @returns La sessione chiusa.
   */
  endSession(sessionId: string, durationSeconds: number): Promise<VoiceSessionSummary> {
    return this.client.request<VoiceSessionSummary>(
      'POST',
      `/api/v1/voice/sessions/${encodeURIComponent(sessionId)}/end`,
      { duration_seconds: Math.max(0, Math.round(durationSeconds)) },
    );
  }

  /**
   * Minuti di voce del mese del chiamante.
   *
   * @returns L'uso.
   */
  getUsage(): Promise<VoiceUsage> {
    return this.client.request<VoiceUsage>('GET', '/api/v1/voice/usage');
  }

  /**
   * Voci dell'agente e quella scelta dal chiamante.
   *
   * @returns Elenco e scelta.
   */
  getVoices(): Promise<VoiceChoice> {
    return this.client.request<VoiceChoice>('GET', '/api/v1/voice/voices');
  }

  /**
   * Salva la voce del chiamante; vale dalla prossima conversazione.
   *
   * @param voice Chiave della voce.
   * @returns Elenco e scelta aggiornata.
   */
  setVoice(voice: string): Promise<VoiceChoice> {
    return this.client.request<VoiceChoice>('PUT', '/api/v1/voice/preference', { voice });
  }

  /**
   * Storico delle conversazioni sul set.
   *
   * @param setId Set.
   * @param limit Conversazioni massime.
   * @returns Le conversazioni, dalla più recente.
   */
  async listSessions(setId: string, limit = 20): Promise<VoiceSessionSummary[]> {
    const page = await this.client.request<{ items: VoiceSessionSummary[] }>(
      'GET',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/voice/sessions?limit=${limit}`,
    );
    return page.items;
  }
}
